import { createTheme } from "@mui/material/styles";

/*
 * WARNING: This file should be used only for static
 * properties, because the sxprops method of styling
 * causes a regeneration of style objects on every page
 * modification. While browsers are tuned for this, MUI's
 * code is not, and it causes a significant performance drop.
 * There are also some CSS effects and properties that are
 * not supported.
 */

/**
 * Browser font sizes are 16px by default. We treat 1rem as 16px.
 * This means that 1rem = 16px, 2rem = 32px, etc.
 *
 * When specifying font sizes and line heights, use rems, not pixels.
 */

const theme = createTheme({
  typography: {
    fontFamily: ["Karla"].join(","),
    h1: {
      fontSize: "1.875rem", // 30px
      lineHeight: "2.5rem", // 40px
      fontWeight: 700,
    },
    h2: {
      fontSize: "1.5rem", // 24px
      lineHeight: "2rem", // 32px
      fontWeight: 700,
    },
    h3: {
      fontSize: "1.25rem", // 20px
      lineHeight: "1.75rem", // 28px
      fontWeight: 500,
    },
    h4: {
      fontSize: "1.1875rem", // 19px
      lineHeight: "1.375rem", // 22px
      fontWeight: 700,
    },
    h5: {
      fontSize: "1.125rem", // 18px
      lineHeight: "1.25rem", // 20px
      fontWeight: 500,
    },
    h6: {
      fontSize: "1.0625rem", // 17px
      lineHeight: "1.125rem", // 18px
      fontWeight: 500,
    },
    body1: {
      fontSize: "1rem", // 16px
      lineHeight: "1.375rem", // 22px
    },
    body2: {
      fontSize: ".875rem", // 14px
      lineHeight: "1.25rem", // 20px
      fontWeight: 400,
    },
  },
  palette: {
    primary: {
      dark: "#232533",
      main: "#48036A",
      light: "#662D91",
      blue200: "#00A0FF",
      blue100: "#5CC2FF",
      purple10: "#F0EAF4",
      purple5: "#F7F5FA",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#ffffff",
      contrastText: "#48036A",
      darkPurple60: "#9168A6",
      darkPurple40: "#B69AC3",
      darkPurple20: "#DACDE1",
      darkPurple10: "#EDE6F0",
      darkPurple5: "#F6F2F8",
      lightPurple60: "#A381BD",
      lightPurple40: "#C2ABD3",
      lightPurple20: "#E0D5E9",
      blue60: "#9DDAFF",
      blue40: "#BEE7FF",
      blue20: "#DEF3FF",
      blue10: "#EFF9FF",
      blue5: "#F7FCFF",
      dark60: "#797C8E",
      dark40: "#A6A8B4",
      dark20: "#D2D3D9",
      dark10: "#E9E9EC",
      dark5: "#F4F4F6",
      dark2: "#FBFBFB",
    },
    info: {
      dark: "#48036A",
      main: "#5CC2FF",
      light: "#5CC2FF",
      contrastText: "#ffffff",
    },
    default: {
      main: "#DFE2E6",
      green: "#03CEA5",
      red: "#FF6480",
      orange: "#FFAA5C",
      borderGray: "#DFE2E6",
    },
    text: {
      primary: "#000000",
      secondary: "#4d4d4d",
    },
    background: {
      default: "#ffffff",
    },
  },
  shadows: [
    "none",
    "0px 0px 1px rgba(0, 0, 0, 0.04), 0px 0px 2px rgba(0, 0, 0, 0.06), 0px 4px 8px rgba(0, 0, 0, 0.04)",
    "0px 0px 4px 0px rgba(0,0,0,0.1)",
    "0px 0px 4px 0px rgba(0,0,0, 0.15)",
    "0px 2px 4px -1px rgba(0,0,0,0.02),0px 4px 5px 0px rgba(0,0,0,0.014),0px 1px 10px 0px rgba(0,0,0,0.12)",
    "0px 3px 5px -1px rgba(0,0,0,0.02),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)",
    "0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)",
    "0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)",
    "0px 5px 5px -3px rgba(0,0,0,0.02),0px 8px 10px 1px rgba(0,0,0,0.014),0px 3px 14px 2px rgba(0,0,0,0.12)",
    "0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)",
    "0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)",
    "0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)",
    "0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)",
    "0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)",
    "0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)",
    "0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)",
    "0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)",
    "0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)",
    "0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)",
    "0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)",
    "0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)",
    "0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)",
    "0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)",
    "0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)",
    "0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)",
  ],

  props: {
    // Name of the component ⚛️
    MuiButtonBase: {
      // The properties to apply
      disableRipple: true, // No more ripple, on the whole application!
    },
  },
});
export default theme;
